import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import useMediaQuery from "@mui/material/useMediaQuery";
import BdrModal from "../BdrModal/BdrModal";

/**
 * BdrSelectPopup — a dropdown whose list is a real DOM element.
 *
 * Reach for this when a list can GROW: invoices and quotes for a client, an
 * assignee roster, hours. A native <select> is the right default everywhere
 * else and this should not replace it wholesale.
 *
 * Why it exists: a native <select>'s option list is an OS-drawn popup, not
 * part of the page. CSS cannot size, cap or clip it, and it is not bounded by
 * the modal or even the browser window — with ~15 invoices the list ran off
 * the top of the screen. Only `option`'s background and colour cross that
 * boundary, which is why it could be made dark but not made to fit.
 *
 * Three things here are load-bearing, each one a bug if removed:
 *
 *  1. PORTALLED to <body>. `.bdr-modal-body` is the modal's scroller
 *     (`overflow-y: auto`), so a list rendered inline would be clipped by it.
 *     Position is therefore `fixed`, computed from the trigger's rect.
 *
 *  2. mousedown does NOT propagate. A React portal bubbles events through the
 *     REACT tree, not the DOM tree — so a click on this list reaches the
 *     modal overlay's backdrop handler, which sees a target outside the panel
 *     and closes the modal. Picking an option would dismiss the whole form.
 *
 *  3. FOCUS STAYS ON THE TRIGGER. The active option is announced with
 *     `aria-activedescendant` rather than by moving focus into the list. The
 *     list is outside the modal panel, so focusing it would fight BdrModal's
 *     Tab trap, which pulls focus back inside.
 *
 * TWO PRESENTATIONS, ONE COMPONENT
 *
 * Below 900px the list opens as a bottom SHEET instead of an anchored popup:
 * a BdrModal at `align="bottom"`, full width to 500px, with taller rows.
 *
 * That is not decoration. Replacing the native <select> app-wide meant giving
 * up the OS picker, which on a phone is a large, thumb-reachable, scrollable
 * surface — a small floating list anchored to a 30px control is a genuinely
 * worse control, not just a differently-styled one. The sheet is the part of
 * the native behaviour worth keeping, rebuilt so it can carry our styling.
 *
 * Sheet mode hands the shell everything it already does properly — backdrop,
 * Esc, focus trap, counted scroll lock — so the only thing that differs here
 * is where the rows are drawn.
 */

/* The canonical 900px breakpoint. Below it, pointers are usually thumbs. */
const SHEET_MAX_WIDTH = 900;

const GAP = 4;
const VIEWPORT_MARGIN = 8;
const MAX_HEIGHT = 320;
const MIN_HEIGHT = 120;

const BdrSelectPopup = ({
  options,
  value,
  onChange,
  name,
  id,
  placeholder,
  disabled,
  className,
  describedBy,
  invalid,
  /** The field's label. Only used as the sheet's title on a narrow screen —
      the anchored list has the field right above it and needs no heading. */
  label,
  /** Native tooltip on the trigger. Carried because a disabled dropdown often
      needs to say WHY — "already referred by a BDR user, cannot change". */
  title,
  /**
   * Size the list to its CONTENT, using the trigger's width only as a floor.
   *
   * The default pins the list to the trigger's width, which is right for a
   * field-width select. It is wrong for a compact trigger — the hour picker is
   * about 55px, so "9 PM" wrapped onto two lines. Leave this off where the
   * trigger is already wide enough for its labels.
   */
  fitContent = false,
}) => {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [position, setPosition] = useState(null);
  const triggerRef = useRef(null);
  const listRef = useRef(null);
  const typeaheadRef = useRef({ buffer: "", timer: null });
  const asSheet = useMediaQuery(`(max-width:${SHEET_MAX_WIDTH - 1}px)`);

  // Normalise: callers pass either strings or { value, label }.
  const items = (options || []).map((opt) =>
    typeof opt === "string" ? { value: opt, label: opt } : opt
  );
  // A placeholder is a real selectable row — it is how the field is cleared.
  const rows = placeholder
    ? [{ value: "", label: placeholder, isPlaceholder: true }, ...items]
    : items;

  // A row with `header: true` is a group label — "— Invoices —" — not a
  // choice. It renders, but arrows step over it and it cannot be committed,
  // which is what `<option disabled>` did in the native list.
  const isPickable = (row) => !!row && !row.header;
  const step = (from, delta) => {
    const n = rows.length;
    for (let i = 1; i <= n; i += 1) {
      const next = (from + delta * i + n * Math.abs(delta) * n) % n;
      if (isPickable(rows[next])) return next;
    }
    return from;
  };
  const firstPickable = rows.findIndex(isPickable);
  const lastPickable = rows.length - 1 - [...rows].reverse().findIndex(isPickable);

  const selectedIndex = rows.findIndex(
    (r) => isPickable(r) && String(r.value) === String(value)
  );
  const selectedLabel =
    selectedIndex >= 0 ? rows[selectedIndex].label : placeholder || "";

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const below = window.innerHeight - rect.bottom - GAP - VIEWPORT_MARGIN;
    const above = rect.top - GAP - VIEWPORT_MARGIN;
    // Prefer below; flip only when below genuinely cannot hold a usable list
    // AND above is roomier. Flipping for a few pixels is worse than scrolling.
    const flip = below < MIN_HEIGHT && above > below;
    const room = Math.max(flip ? above : below, MIN_HEIGHT);
    setPosition({
      left: rect.left,
      width: rect.width,
      maxHeight: Math.min(MAX_HEIGHT, room),
      // Anchoring by `bottom` when flipped means the list grows upward from
      // the trigger without needing its rendered height first.
      ...(flip
        ? { bottom: window.innerHeight - rect.top + GAP }
        : { top: rect.bottom + GAP }),
    });
  }, []);

  useLayoutEffect(() => {
    // A sheet is pinned to the viewport, so there is nothing to anchor and
    // nothing to re-anchor on scroll.
    if (!open || asSheet) return undefined;
    place();
    // `true` = capture, so this also fires for scrolls inside the modal body,
    // which does not bubble a scroll event to window.
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, asSheet, place]);

  // Bring the active row into view as the arrows move through a long list.
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const node = listRef.current?.children?.[activeIndex];
    node?.scrollIntoView({ block: "nearest" });
  }, [open, activeIndex]);

  // Sheet only: take focus off the panel and put it on the list, so the arrow
  // keys work without the user first having to Tab to find it.
  useEffect(() => {
    if (!open || !asSheet) return undefined;
    const raf = requestAnimationFrame(() => listRef.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, [open, asSheet]);

  useEffect(() => {
    // The sheet has a real backdrop; BdrModal owns dismissal there. Leaving
    // this bound would close the sheet on the same mousedown that opened it.
    if (!open || asSheet) return undefined;
    const onDocMouseDown = (event) => {
      if (triggerRef.current?.contains(event.target)) return;
      if (listRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open, asSheet]);

  const openList = () => {
    if (disabled) return;
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : firstPickable);
    setOpen(true);
  };

  const commit = (index) => {
    const row = rows[index];
    if (!isPickable(row)) return;
    setOpen(false);
    triggerRef.current?.focus();
    /*
     * Shaped like a native change event so existing handlers — which all read
     * `event.target.value` — keep working unchanged.
     *
     * String() is load-bearing, not tidiness. A native <select> ALWAYS hands
     * back a string, however the option was declared. This has to match that
     * exactly or it is not a drop-in: a caller comparing `value === "5"`, or
     * indexing with a value it assumes is a string, would silently stop
     * matching the moment its list was switched over. The number/string seam
     * between MUI's Select and a native one already cost three components a
     * silent "All option stops working" bug; there is no reason to reopen it.
     */
    onChange?.({ target: { name, value: String(row.value) } });
  };

  const typeahead = (char) => {
    const state = typeaheadRef.current;
    clearTimeout(state.timer);
    state.buffer += char.toLowerCase();
    state.timer = setTimeout(() => {
      state.buffer = "";
    }, 600);
    const hit = rows.findIndex(
      (r) =>
        isPickable(r) &&
        String(r.label).toLowerCase().startsWith(state.buffer)
    );
    if (hit >= 0) setActiveIndex(hit);
  };

  const handleKeyDown = (event) => {
    if (disabled) return;
    const { key } = event;

    if (!open) {
      if (key === "Enter" || key === " " || key === "ArrowDown" || key === "ArrowUp") {
        event.preventDefault();
        openList();
      }
      return;
    }

    switch (key) {
      case "Escape":
        event.preventDefault();
        // Stop here: BdrModal also closes on Escape, and the list closing
        // should not take the modal with it.
        event.stopPropagation();
        setOpen(false);
        break;
      case "Tab":
        setOpen(false);
        break;
      case "ArrowDown":
        event.preventDefault();
        setActiveIndex((i) => step(i, 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        setActiveIndex((i) => step(i, -1));
        break;
      case "Home":
        event.preventDefault();
        setActiveIndex(firstPickable);
        break;
      case "End":
        event.preventDefault();
        setActiveIndex(lastPickable);
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        commit(activeIndex);
        break;
      default:
        if (key.length === 1) typeahead(key);
        break;
    }
  };

  const listId = `${id}-listbox`;

  // The rows are identical in both presentations — only the box around them
  // changes. Anything added here (a header row, a disabled state) therefore
  // reaches the sheet and the anchored list at the same time.
  const optionRows = rows.map((row, index) =>
    row.header ? (
      <li key={`h-${index}`} role="presentation" className="bdr-select-header">
        {row.label}
      </li>
    ) : (
      <li
        key={`${row.value}-${index}`}
        id={`${listId}-${index}`}
        role="option"
        aria-selected={index === selectedIndex}
        className={[
          "bdr-select-option",
          index === activeIndex ? "is-active" : "",
          index === selectedIndex ? "is-selected" : "",
          row.isPlaceholder ? "is-placeholder" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        /* mousedown, not click: the trigger keeps focus, and a click would
           land after the document mousedown handler has already closed the
           list. */
        onMouseDown={(event) => {
          event.preventDefault();
          commit(index);
        }}
        onMouseEnter={() => setActiveIndex(index)}
      >
        {row.label}
      </li>
    )
  );

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        name={name}
        disabled={disabled}
        title={title}
        className={className}
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={handleKeyDown}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={
          open && activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined
        }
        aria-invalid={invalid ? true : undefined}
        aria-describedby={describedBy}
      >
        <span
          className={
            selectedIndex > 0 || (!placeholder && selectedIndex === 0)
              ? "bdr-select-value"
              : "bdr-select-value bdr-select-value--placeholder"
          }
        >
          {selectedLabel}
        </span>
      </button>

      {/* ── narrow screens: a bottom sheet ── */}
      {asSheet && (
        <BdrModal
          open={open}
          onClose={() => setOpen(false)}
          align="bottom"
          /* Above whatever modal hosts the field — see LAYER_CLASS.popover. */
          layer="popover"
          maxWidth="500px"
          title={label || "Select"}
          ariaLabel={label ? undefined : "Select an option"}
          panelClassName="bdr-select-sheet"
        >
          {/*
            Focusable, and it carries the key handler. In the anchored case the
            trigger keeps focus and drives the list by `aria-activedescendant`;
            here BdrModal moves focus into the panel, so the trigger never sees
            another keystroke. Without this the rows would be reachable by
            pointer only — `<li role="option">` is not focusable — which would
            leave a keyboard user on a narrow window with no way to choose.
          */}
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            tabIndex={0}
            onKeyDown={handleKeyDown}
            aria-activedescendant={
              activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined
            }
            className="bdr-select-list bdr-select-list--sheet"
          >
            {optionRows}
          </ul>
        </BdrModal>
      )}

      {/* ── wide screens: anchored to the trigger ── */}
      {!asSheet &&
        open &&
        position &&
        createPortal(
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            className={`bdr-select-list${
              fitContent ? " bdr-select-list--fit" : ""
            }`}
            style={{
              left: position.left,
              ...(fitContent
                ? { minWidth: position.width }
                : { width: position.width }),
              maxHeight: position.maxHeight,
              ...(position.top !== undefined
                ? { top: position.top }
                : { bottom: position.bottom }),
            }}
            /* See note 2 at the top — without this, picking an option reads
               as a backdrop click and closes the host modal. The sheet needs
               no equivalent: its own overlay is a real modal, and BdrModal's
               backdrop check asks the DOM which overlay a target belongs to. */
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
          >
            {optionRows}
          </ul>,
          document.body
        )}
    </>
  );
};

export default BdrSelectPopup;
